import { Component, OnInit } from '@angular/core';
import { FcGenericItemModalBaseComponent, FcListValidationService } from '../../generic';
// eslint-disable-next-line no-restricted-imports
import { FormControl, FormGroup, UntypedFormArray } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcMacroGroup, FcMacroGroupDto, FormGroupValidationRules, MacroGroupSecurityFormGroupDto, RelMacroGroupSecurityDto } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-macro-group-list-modal',
  templateUrl: './fc-macro-group-list-modal.component.html'
})
export class FcMacroGroupListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-macro-group';

  public userOrUserGroupUidFormControlName = 'userOrUserGroupUidFormControl';

  public idControl: FormControl = new FormControl<string>('');
  public externalIdControl: FormControl = new FormControl<string>('');
  public nameControl: FormControl = new FormControl<string>('');
  public notesControl: FormControl = new FormControl<string>('');
  public inactiveControl: FormControl = new FormControl<boolean>(false);
  public userDefinedControl: FormControl = new FormControl<boolean>(true);

  public securityTabFormArray: UntypedFormArray = new UntypedFormArray([]);
  userOrUserGroupIds: string[] = [];

  public form: FormGroup = new FormGroup({
    id: this.idControl,
    externalId: this.externalIdControl,
    name: this.nameControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
    relMacroGroupSecurityList: this.securityTabFormArray,
  });

  public listItem: FcMacroGroupDto = {
    Id: '',
    ExternalID: '',
    Inactive: false,
    Name: '',
    Notes: '',
    UserDefined: true,
    RelMacroGroupSecurityList: [],
  };

  public securityListItem: RelMacroGroupSecurityDto = {
    MacroGroupSecurityUid: '',
    MacroGroupUid: this.listItem.Id,
    UserOrUserGroupUid: '',
    NewAllowed: false,
    ModifyAllowed: false,
    DeleteAllowed: false,
    IsLockedBySystem: false,
    UserDefined: 1,
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    ExternalID: {
      MaxLength: 25,
    },
    Name: {
      IsRequired: true,
      MaxLength: 100,
    },
    Notes: {
      MaxLength: 65535
    },
    UserOrUserGroupUid: {
      IsRequired: true,
    },
  };

  override ngOnInit(): void {
    super.ngOnInit();

    this.loadListItem(this.listItemDefaultValidation);
  }

  public getGeneralFormErrors(): number {
    let count = 0;
    Object.keys(this.form.controls).forEach(x => {
      if (this.form.controls[x].invalid && !x.includes('Security')) {
        count++;
      }
    });
    return count;
  }

  public getSecurityErrors(): number {
    return this.securityTabFormArray.controls.filter(x => x.invalid).length;
  }

  public isSecurityEditDisabled(): boolean {
    const val = this.systemGuid.MacroGroupSystemUid.toUpperCase() === this.listItem.Id.toUpperCase();
    return val;
  }

  public isSecurityEditEnabled(): boolean {
    const val = this.systemGuid.MacroGroupSystemUid.toUpperCase() !== this.listItem.Id.toUpperCase();
    return val;
  }

  override setForm(): void {
    this.idControl.setValue(this.listItem?.Id);
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem?.ExternalID);

    // Name Control
    const isNameControlDisabled = !this.listItem.UserDefined;
    if (isNameControlDisabled) {
      this.nameControl.disable();
    }
    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem?.Name);

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem?.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    // User Defined Control
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined);

    this.securityUserGroupsToFormGroups();
  }

  public override save(): void {
    this.isSubmitted = true;
    const values = this.form.getRawValue();
    const securityList: MacroGroupSecurityFormGroupDto[] = values.relMacroGroupSecurityList;
    const relMacroSecurityList = securityList.map(this.convertMacroGroupSecurityFromGroupDtoToRelMacroGroupSecurityDto);
    const itemDto: FcMacroGroup = {
      ...(values.id && { Id: values.id }),
      ExternalID: values.externalId ?? '',
      Name: values.name,
      Notes: values.notes,
      Inactive: values.inactive,
      UserDefined: values.userDefined,
      RelMacroGroupSecurityList: relMacroSecurityList,
    };

    super.save<FcMacroGroup>(itemDto);
  }

  private securityUserGroupsToFormGroups(): void {
    this.listItem.RelMacroGroupSecurityList.forEach(listItem => {
      const securityUserGroupGroupForm = this.addSecurityUserGroupFormGroup(listItem);
      this.securityTabFormArray.push(securityUserGroupGroupForm);
    });
  }

  private addSecurityUserGroupFormGroup(item: RelMacroGroupSecurityDto): FormGroup {
    this.userOrUserGroupIds.push(item.UserOrUserGroupUid);

    const userOrUserGroupUidFormControl = new FormControl<string>(item.UserOrUserGroupUid);
    userOrUserGroupUidFormControl.setValidators(this.fcListValidation.getValidators('UserOrUserGroupUid'));

    const newFormGroup = new FormGroup({
      macroGroupSecurityUidFormControl: new FormControl<string>(item.MacroGroupSecurityUid),
      macroGroupUidFormControl: new FormControl<string>(this.listItem.Id),
      userOrUserGroupUidFormControl,
      isNewSelectedFormControl: new FormControl<boolean>(item.NewAllowed),
      isModifySelectedFormControl: new FormControl<boolean>(item.ModifyAllowed),
      isDeleteSelectedFormControl: new FormControl<boolean>(item.DeleteAllowed),
      isLockedBySystemFormControl: new FormControl<boolean>(item.IsLockedBySystem),
      userDefinedFormControl: new FormControl<number>(item.UserDefined),
    });

    if (this.isSecurityEditDisabled()) {
      newFormGroup.disable();
    }

    return newFormGroup;
  }

  public addRow(): void {
    if (!this.isSecurityEditDisabled()) {
      const securityUserGroupGroupForm = this.addSecurityUserGroupFormGroup(this.securityListItem);
      this.securityTabFormArray.push(securityUserGroupGroupForm);
    }
  }

  public deleteRow(index: number): void {
    if (!this.isSecurityEditDisabled()) {
      this.securityTabFormArray.removeAt(index);
      this.form.markAsDirty();
    }
  }

  private convertMacroGroupSecurityFromGroupDtoToRelMacroGroupSecurityDto(dto: MacroGroupSecurityFormGroupDto): RelMacroGroupSecurityDto {
    const raw = dto.userOrUserGroupUidFormControl as unknown;
    const userOrUserGroupUid = Array.isArray(raw) ? (raw[0] ?? '') : (typeof raw === 'string' ? raw : '');
    return {
      MacroGroupSecurityUid: dto.macroGroupSecurityUidFormControl,
      MacroGroupUid: dto.macroGroupUidFormControl,
      UserOrUserGroupUid: userOrUserGroupUid,
      NewAllowed: dto.isNewSelectedFormControl,
      ModifyAllowed: dto.isModifySelectedFormControl,
      DeleteAllowed: dto.isDeleteSelectedFormControl,
      IsLockedBySystem: dto.isLockedBySystemFormControl,
      UserDefined: dto.userDefinedFormControl,
    };
  }
}
